/**
 * @file Web environment React context (T0.7).
 *
 * `main.tsx` validates the build-time environment once via `readWebEnv` and
 * provides it here. Components read it through `useWebEnv()` instead of
 * touching `import.meta.env` directly, which keeps them testable: a test
 * renders `<WebEnvProvider value={fakeEnv}>` and never has to stub the bundler.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { WebEnv } from "./env";

const WebEnvContext = createContext<WebEnv | null>(null);

export function WebEnvProvider({
  value,
  children,
}: {
  readonly value: WebEnv;
  readonly children: ReactNode;
}) {
  return <WebEnvContext.Provider value={value}>{children}</WebEnvContext.Provider>;
}

/** Read the validated web environment. Throws if used outside the provider. */
export function useWebEnv(): WebEnv {
  const value = useContext(WebEnvContext);
  if (value === null) {
    throw new Error("useWebEnv() must be called inside a <WebEnvProvider>.");
  }
  return value;
}
